// 選択ハンドルのうち、回転ハンドル用のドラッグハンドラ。
//
// - pointerdown でドラッグ開始（基準角度を捕捉）
// - pointermove でオブジェクト中心とポインタ位置から角度差分を計算し、対象 DOM の
//   `transform: rotate(deg)` を即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 rotation を transformObject op として送信し永続化
// - pointercancel（ブラウザ / OS による中断、2 本指ピンチの開始など）では確定せず、
//   直書きした回転プレビューをストア値へ巻き戻して破棄する
//
// Shift 押下時は 15° 単位にスナップする。

import { useEffect, useRef, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useConnectorDragStore } from "../store/connector-drag-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  /** オブジェクトのステージ座標における中心。回転計算の原点になる。 */
  readonly centerX: number;
  readonly centerY: number;
  readonly initialRotation: number;
  /** プレビュー対象の DOM 要素。`transform: rotate(...)` を直接書き換える。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const SNAP_ANGLE_DEG = 15;
// 他参加者向けゴースト中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const TRANSIENT_THROTTLE_MS = 50;

export function useRotationHandle(options: Options): void {
  const { handleRef, objectId, dispatcher, centerX, centerY, initialRotation, targetRef } =
    options;
  // 参照専用（埋め込み）では回転を無効化する。
  const readOnly = useCanvasStore((s) => s.readOnly);

  // ドラッグ状態は effect のクロージャではなく ref に保持する。
  // dispatcher の identity 変化や同一オブジェクトへのリモート更新でドラッグ中に effect が
  // 再バインドされても、進行中のドラッグ（特に pointerup での確定）を失わないため
  // （useResizeHandle と同じ実障害対策。回転中心・開始回転もドラッグ開始時に固定する）。
  const draggingRef = useRef<{
    pointerId: number;
    startAngleDeg: number;
    baseRotation: number;
    baseCenterX: number;
    baseCenterY: number;
    // 最後の pointermove プレビューで確定した回転角。lostpointercapture の確定に使う。
    lastPreviewRotation: number | null;
    // 他参加者向けゴースト中継を間引くための直近送信時刻（ms）。
    lastTransientAt: number;
  } | null>(null);

  // アンマウント時の後始末（選択解除・種別切替などで選択オーバーレイごと消える経路）。
  // 下の effect の teardownOrphanDrag は「effect が再実行されて readOnly / handle 無しの経路に
  // 入ったとき」しか走らず、unmount では cleanup（listener 解除）だけが走って進行中ドラッグと
  // コネクタ用ライブ枠が残留し得る。空依存（objectId のみ）effect の cleanup は listener の
  // 再バインドでは走らず真のアンマウント時のみ実行されるため、「effect 再バインドを跨いで
  // 進行中ドラッグを維持する」設計（draggingRef）と両立したまま残留だけを確実に片付けられる
  // （選択オーバーレイは key={id} で張り替えるため objectId はフック寿命内で不変）。
  useEffect(() => {
    return () => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      // 直書きした transform（回転）プレビューをストア値へ巻き戻す。オーバーレイが消えた後は
      // 自己修復（canvas-selection-overlay）も走らないため、ここで戻さないと残留する。
      revertRotationPreview(targetRef.current, objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
    };
  }, [objectId, targetRef]);

  useEffect(() => {
    // readOnly 切替やハンドル消滅で listener を張らない経路に入るとき、進行中ドラッグが
    // 残っていれば破棄する（listener が無いままでは pointerup / lostpointercapture を拾えず
    // 確定不能）。コネクタ追従のライブ枠も片付け、選択オーバーレイの自己修復スキップが
    // 当該オブジェクトへ残留しないようにする（use-object-drag の teardown と同じ方針）。
    const teardownOrphanDrag = (): void => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      // unmount 経路の後始末と同様に、直書きプレビューもストア値へ巻き戻す。
      revertRotationPreview(targetRef.current, objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
    };
    if (readOnly) {
      teardownOrphanDrag();
      return undefined;
    }
    const handle = handleRef.current;
    if (!handle) {
      teardownOrphanDrag();
      return undefined;
    }

    const angleFromClient = (
      clientX: number,
      clientY: number,
      cX: number,
      cY: number
    ): number => {
      // クライアント座標 → ステージ座標
      const view = useCanvasStore.getState().view;
      const stageX = (clientX - view.panX) / view.zoom;
      const stageY = (clientY - view.panY) / view.zoom;
      const dx = stageX - cX;
      const dy = stageY - cY;
      // atan2 は X 軸右方向を 0 として反時計回りに増えるので、UI の上方向を 0
      // とする 12 時起点（時計回り正）に変換する: angle = atan2(dy, dx) + 90°
      return Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      draggingRef.current = {
        pointerId: event.pointerId,
        startAngleDeg: angleFromClient(event.clientX, event.clientY, centerX, centerY),
        baseRotation: initialRotation,
        baseCenterX: centerX,
        baseCenterY: centerY,
        lastPreviewRotation: null,
        lastTransientAt: 0,
      };
      // jsdom などポインタキャプチャ未実装の環境でも落ちないよう存在チェックする。
      handle.setPointerCapture?.(event.pointerId);
      event.stopPropagation();
    };

    const computeRotation = (
      dragging: NonNullable<typeof draggingRef.current>,
      event: PointerEvent
    ): number => {
      const currentAngle = angleFromClient(
        event.clientX,
        event.clientY,
        dragging.baseCenterX,
        dragging.baseCenterY
      );
      let next = dragging.baseRotation + (currentAngle - dragging.startAngleDeg);
      if (event.shiftKey) {
        next = Math.round(next / SNAP_ANGLE_DEG) * SNAP_ANGLE_DEG;
      }
      // -180..180 の範囲へ正規化
      next = ((next % 360) + 540) % 360 - 180;
      return next;
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      const next = computeRotation(dragging, event);
      dragging.lastPreviewRotation = next;
      const target = targetRef.current;
      if (target) {
        target.style.transform = `rotate(${next}deg)`;
      }
      updateSelectionBoxRotation(next);

      // コネクタ（接続矢印）・接続ドットのリアルタイム追従用に、確定前の回転角をライブ枠へ反映する。
      const docObject = useCanvasDocStore.getState().objects[objectId];
      if (docObject) {
        useConnectorDragStore.getState().setFrames({
          [objectId]: {
            x: docObject.x,
            y: docObject.y,
            w: docObject.w,
            h: docObject.h,
            rotation: next,
          },
        });
      }

      // 他参加者へ回転中ゴーストを中継する（一定間隔で間引く）。
      const now = Date.now();
      if (now - dragging.lastTransientAt >= TRANSIENT_THROTTLE_MS) {
        dragging.lastTransientAt = now;
        dispatcher.previewTransforms([{ objectId, transform: { rotation: next } }]);
      }
    };

    const commitRotation = (rotation: number): void => {
      dispatcher.transformObject(objectId, { rotation });
      // 確定（doc store へ反映）後にライブ枠を破棄する。コネクタは確定済み doc 値へ収束する。
      useConnectorDragStore.getState().clearFrames([objectId]);
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      // 最後の pointermove プレビュー角をそのまま確定に使う（useResizeHandle の lastPreviewFrame と
      // 同じ理由）。リリース直前に Shift だけ離した場合などに、プレビューで見せたスナップ済み角と
      // 確定値がずれないようにする。プレビューが一度も走っていない（pointerdown 直後に pointerup）
      // 場合のみ、現在のイベントから計算した角へフォールバックする。
      const next = dragging.lastPreviewRotation ?? computeRotation(dragging, event);
      draggingRef.current = null;
      if (handle.hasPointerCapture?.(event.pointerId)) {
        handle.releasePointerCapture?.(event.pointerId);
      }
      commitRotation(next);
    };

    // pointercancel（ブラウザ / OS による中断、2 本指ピンチ開始時の取り消し）では commitRotation を
    // 呼ばず、直書きした回転プレビューを巻き戻して状態を初期化する。pointercancel は利用者が指や
    // ボタンを離した合図ではないため、確定すると意図しない角度で回転が確定してしまう。
    // 状態を先に捨ててから pointer capture を解放し、解放に伴う lostpointercapture が届いても
    // 確定へ進まないようにする。
    const onPointerCancel = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      draggingRef.current = null;
      if (handle.hasPointerCapture?.(event.pointerId)) {
        handle.releasePointerCapture?.(event.pointerId);
      }
      revertRotationPreview(targetRef.current, objectId);
      revertSelectionBoxRotation(objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
    };

    // pointerup を経ずにポインタキャプチャを失った場合の受け皿（useResizeHandle と同じ）。
    // 最後のプレビュー角を確定し、直接 DOM プレビューだけが残る乖離を残さない。
    const onLostPointerCapture = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      const next = dragging.lastPreviewRotation;
      draggingRef.current = null;
      if (next !== null) {
        commitRotation(next);
      } else {
        // 一度もプレビューしていない（何も変えていない）ので確定せず破棄だけする。
        useConnectorDragStore.getState().clearFrames([objectId]);
      }
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerCancel);
    handle.addEventListener("lostpointercapture", onLostPointerCapture);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerCancel);
      handle.removeEventListener("lostpointercapture", onLostPointerCapture);
    };
  }, [handleRef, objectId, dispatcher, centerX, centerY, initialRotation, readOnly, targetRef]);
}

// 中断した回転の直書き transform プレビューをストア値へ巻き戻す（未回転は inline transform なし）。
function revertRotationPreview(target: HTMLElement | null, objectId: string): void {
  if (!target) return;
  const obj = useCanvasDocStore.getState().objects[objectId];
  if (!obj) return;
  target.style.transform = obj.rotation ? `rotate(${obj.rotation}deg)` : "";
}

// 中断した回転で選択ボックス（枠とつまみ）へ直書きした transform をストア値へ巻き戻す。
// React は仮想スタイルが不変な限り書き戻さないため、ここで戻さないと本体だけ元の角度へ復帰し、
// 枠とつまみだけが中断時点の角度で取り残される。未回転は inline transform なしへ揃える。
function revertSelectionBoxRotation(objectId: string): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  const obj = useCanvasDocStore.getState().objects[objectId];
  if (!obj) return;
  box.style.transform = obj.rotation ? `rotate(${obj.rotation}deg)` : "";
}

function updateSelectionBoxRotation(rotation: number): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  box.style.transform = `rotate(${rotation}deg)`;
}
